import { useState, useEffect, useRef } from "react";
import {
  Stack,
  Box,
  Flex,
  Text,
  Button,
  Badge,
  Grid,
  GridItem,
  VStack,
  HStack,
  Divider,
  Avatar,
  Heading,
  Textarea,
  useToast,
  Skeleton,
  IconButton,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Select,
  Card,
  CardBody,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  FormControl,
  FormLabel,
  useDisclosure,
} from "@chakra-ui/react";
import { useParams, useNavigate } from "react-router-dom";
import BreadCrumbMaker from "../../../components/BreadCrumbMaker";
import { FiArrowLeft, FiSend, FiMoreVertical, FiRefreshCw, FiUser } from "react-icons/fi";
import {
  fetchTicketById,
  fetchAdminUsers,
  fetchRoles,
  assignTicket,
  updateTicket,
  closeTicket,
  addComment,
} from "./TicketActions.jsx";

const TicketDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const commentsEndRef = useRef(null);

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [ticket, setTicket] = useState(null);
  const [comments, setComments] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [newComment, setNewComment] = useState("");
  const [selectedAdmin, setSelectedAdmin] = useState("");
  const [statusToUpdate, setStatusToUpdate] = useState("");

  // Assign modal states
  const { isOpen: isAssignOpen, onOpen: onAssignOpen, onClose: onAssignClose } = useDisclosure();
  const [roles, setRoles] = useState([]);
  const [selectedRole, setSelectedRole] = useState("");
  const [assignModalAdmins, setAssignModalAdmins] = useState([]);
  const [selectedAssignAdmin, setSelectedAssignAdmin] = useState("");

  const statusColorMap = {
    Open: "red",
    "In Progress": "yellow",
    "Waiting for Customer": "orange",
    Resolved: "green",
    Closed: "gray",
  };

  const priorityColorMap = {
    Urgent: "red",
    High: "orange",
    Medium: "blue",
    Low: "gray",
  };

  const statusOptions = ["Open", "In Progress", "Waiting for Customer", "Resolved", "Closed"];
  const priorityOptions = ["Low", "Medium", "High", "Urgent"];

  // Fetch ticket details
  const fetchTicketData = async () => {
    setLoading(true);
    try {
      const response = await fetchTicketById(id);
      
      if (response.success) {
        const ticketData = response.data;
        setTicket(ticketData);
        setComments(ticketData.comments || []);
        setStatusToUpdate(ticketData.status);
      }
    } catch (error) {
      console.error("Error fetching ticket:", error);
      toast({
        title: "Error",
        description: error?.response?.data?.message || "Failed to fetch ticket details",
        status: "error",
        duration: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  // Fetch admins
  const fetchAdmins = async () => {
    try {
      const response = await fetchAdminUsers();
      if (response.success) {
        setAdmins(response.data || []);
      }
    } catch (error) {
      console.error("Error fetching admins:", error);
    }
  };

  useEffect(() => {
    fetchTicketData();
    fetchAdmins();
  }, [id]);

  // Fetch roles for assign modal
  useEffect(() => {
    const fetchRolesData = async () => {
      try {
        const response = await fetchRoles();
        if (response.success) {
          setRoles(response.data || []);
        }
      } catch (error) {
        console.error("Error fetching roles:", error);
      }
    };
    fetchRolesData();
  }, []);

  // Fetch admins when role is selected in modal
  useEffect(() => {
    const fetchAdminsByRole = async () => {
      if (selectedRole) {
        try {
          const response = await fetchAdminUsers(selectedRole);
          if (response.success) {
            setAssignModalAdmins(response.data || []);
          }
        } catch (error) {
          console.error("Error fetching admins:", error);
        }
      } else {
        setAssignModalAdmins([]);
      }
      setSelectedAssignAdmin("");
    };
    fetchAdminsByRole();
  }, [selectedRole]);

  // Open assign modal and set initial values
  const handleOpenAssignModal = () => {
    setSelectedRole(ticket.assignedTo?.role || "");
    setSelectedAssignAdmin(ticket.assignedTo?._id || "");
    if (ticket.assignedTo?.role) {
      fetchAdminUsers(ticket.assignedTo.role).then(response => {
        if (response.success) {
          setAssignModalAdmins(response.data || []);
        }
      });
    }
    onAssignOpen();
  };

  // Handle assign from modal
  const handleAssignFromModal = async () => {
    if (!selectedAssignAdmin) return;
    try {
      await assignTicket(id, selectedAssignAdmin);
      toast({
        title: "Success",
        description: "Ticket assigned successfully",
        status: "success",
        duration: 3000,
      });
      onAssignClose();
      fetchTicketData();
    } catch (error) {
      toast({
        title: "Error",
        description: error?.response?.data?.message || "Failed to assign ticket",
        status: "error",
        duration: 3000,
      });
    }
  };

  // Scroll to bottom when new comment is added
  useEffect(() => {
    if (commentsEndRef.current) {
      commentsEndRef.current.scrollIntoView({ behavior: "smooth" });
    }
  }, [comments]);

  // Handle add comment
  const handleAddComment = async () => {
    if (!newComment.trim()) {
      toast({
        title: "Validation Error",
        description: "Please enter a comment",
        status: "warning",
        duration: 3000,
      });
      return;
    }

    setSubmitting(true);
    try {
      const response = await addComment(id, {
        message: newComment,
        commentType: "Admin Reply",
      });

      if (response.success) {
        setComments([...comments, response.data]);
        setNewComment("");
        toast({
          title: "Success",
          description: "Comment added successfully",
          status: "success",
          duration: 3000,
        });
      }
    } catch (error) {
      toast({
        title: "Error",
        description: error?.response?.data?.message || "Failed to add comment",
        status: "error",
        duration: 3000,
      });
    } finally {
      setSubmitting(false);
    }
  };

  // Handle assign
  const handleAssign = async () => {
    if (!selectedAdmin) return;

    try {
      await assignTicket(id, selectedAdmin);
      toast({
        title: "Success",
        description: "Ticket assigned successfully",
        status: "success",
        duration: 3000,
      });
      fetchTicketData();
    } catch (error) {
      toast({
        title: "Error",
        description: error?.response?.data?.message || "Failed to assign ticket",
        status: "error",
        duration: 3000,
      });
    }
  };

  // Handle update status
  const handleUpdateStatus = async () => {
    if (!statusToUpdate || statusToUpdate === ticket.status) return;

    try {
      await updateTicket(id, { status: statusToUpdate });
      toast({
        title: "Success",
        description: "Status updated successfully",
        status: "success",
        duration: 3000,
      });
      fetchTicketData();
    } catch (error) {
      toast({
        title: "Error",
        description: error?.response?.data?.message || "Failed to update status",
        status: "error",
        duration: 3000,
      });
    }
  };

  // Handle update priority
  const handleUpdatePriority = async (priority) => {
    try {
      await updateTicket(id, { priority });
      toast({
        title: "Success",
        description: "Priority updated successfully",
        status: "success",
        duration: 3000,
      });
      fetchTicketData();
    } catch (error) {
      toast({
        title: "Error",
        description: error?.response?.data?.message || "Failed to update priority",
        status: "error",
        duration: 3000,
      });
    }
  };

  // Handle close ticket
  const handleCloseTicket = async () => {
    try {
      await closeTicket(id);
      toast({
        title: "Success",
        description: "Ticket closed successfully",
        status: "success",
        duration: 3000,
      });
      fetchTicketData();
      navigate("/support/complaints");
    } catch (error) {
      toast({
        title: "Error",
        description: error?.response?.data?.message || "Failed to close ticket",
        status: "error",
        duration: 3000,
      });
    }
  };

  const formatDate = (date) => {
    if (!date) return "-";
    return new Date(date).toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  if (loading) {
    return (
      <Stack p={6} spacing={4}>
        <Skeleton height="40px" />
        <Skeleton height="200px" />
        <Skeleton height="300px" />
      </Stack>
    );
  }

  if (!ticket) {
    return (
      <Stack p={6} spacing={4} align="center" justify="center" minH="50vh">
        <Text fontSize="lg" color="gray.500">Ticket not found</Text>
        <Button leftIcon={<FiArrowLeft />} onClick={() => navigate("/support/complaints")}>
          Back to Complaints
        </Button>
      </Stack>
    );
  }

  return (
    <Stack p={6} gap={4}>
      {/* Breadcrumb */}
      <Flex justify="space-between" align="center">
        <BreadCrumbMaker
          items={[
            { link: "/support", title: "Support Center" },
            { link: "/support/complaints", title: "Complaints" },
            { link: "#", title: ticket.ticketId, on: true },
          ]}
        />
        {/* <HStack>
          <IconButton
            icon={<FiRefreshCw />}
            variant="outline"
            onClick={fetchTicketData}
            aria-label="Refresh"
          />
          <Button leftIcon={<FiArrowLeft />} onClick={() => navigate("/support/complaints")}>
            Back
          </Button>
        </HStack> */}
      </Flex>

      <Grid templateColumns={{ base: "1fr", lg: "2fr 1fr" }} gap={4}>
        {/* Main Content - Comments/Conversation */}
        <GridItem>
          <Box bg="white" borderRadius="xl" shadow="sm" overflow="hidden">
            <Box p={4} borderBottom="1px" borderColor="gray.100">
              <Flex justify="space-between" align="start">
                <Box>
                  <Heading size="md" color="gray.700">{ticket.subject}</Heading>
                  <Text color="gray.500" mt={1}>{ticket.description}</Text>
                </Box>
                
              </Flex>
               {/* <VStack spacing={3} align="stretch"> */}
               <Box p={4} borderBottom="1px" borderColor="gray.100">
               <Flex justify="space-between" align="start">
                  {/* Assign */}
                  {/* <Box>
                    <Text fontSize="sm" color="gray.500" mb={1}>Assign To</Text>
                    <HStack>
                      <Select
                        size="sm"
                        placeholder="Select admin"
                        value={selectedAdmin || ticket.assignedTo?._id || ""}
                        onChange={(e) => setSelectedAdmin(e.target.value)}
                      >
                        {admins.map(admin => (
                          <option key={admin._id} value={admin._id}>
                            {admin.name}
                          </option>
                        ))}
                      </Select>
                      <Button
                        size="sm"
                        colorScheme="blue"
                        onClick={handleAssign}
                        isDisabled={!selectedAdmin || selectedAdmin === ticket.assignedTo?._id}
                      >
                        Assign
                      </Button>
                    </HStack>
                  </Box> */}
                   <Button
                  leftIcon={<FiUser />}
                  size="sm"
                  colorScheme="blue"
                  variant="outline"
                  onClick={handleOpenAssignModal}
                >
                  Assign
                </Button>

                  {/* Update Status */}
                  <Box>
                    {/* <Text fontSize="sm" color="gray.500" mb={1}>Update Status</Text> */}
                    <HStack>
                      <Select
                        size="sm"
                        value={statusToUpdate}
                        onChange={(e) => setStatusToUpdate(e.target.value)}
                      >
                        {statusOptions.map(s => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </Select>
                      <Button
                        size="sm"
                        colorScheme="green"
                        onClick={handleUpdateStatus}
                        isDisabled={!statusToUpdate || statusToUpdate === ticket.status}
                      >
                        Update
                      </Button>
                    </HStack>
                  </Box>
                  </Flex>
                  </Box>
               {/* </VStack> */}
            </Box>

            {/* Comments Section */}
            <Box p={4} maxH="500px" overflowY="auto" bg="gray.50">
              {comments.length === 0 ? (
                <Flex justify="center" align="center" py={8}>
                  <Text color="gray.500">No comments yet</Text>
                </Flex>
              ) : (
                <VStack spacing={4} align="stretch">
                  {comments.map((comment, index) => (
                    <Box
                      key={comment._id || index}
                      bg={comment.commentType === "Internal Note" ? "orange.50" : "white"}
                      p={4}
                      borderRadius="lg"
                      border="1px"
                      borderColor="gray.200"
                    >
                      <Flex justify="space-between" align="start" mb={2}>
                        <HStack>
                          <Avatar size="sm" name={comment.commentedBy?.name || "Unknown"} />
                          <VStack align="start" spacing={0}>
                            <Text fontWeight="medium" fontSize="sm">
                              {comment.commentedBy?.name || "Unknown User"}
                            </Text>
                            <HStack spacing={2}>
                              <Badge
                                size="sm"
                                colorScheme={
                                  comment.commentType === "Admin Reply"
                                    ? "blue"
                                    : comment.commentType === "Internal Note"
                                    ? "orange"
                                    : "green"
                                }
                              >
                                {comment.commentType}
                              </Badge>
                            </HStack>
                          </VStack>
                        </HStack>
                        <Text fontSize="xs" color="gray.500">
                          {formatDate(comment.createdAt)}
                        </Text>
                      </Flex>
                      <Text color="gray.700" pl={10}>{comment.message}</Text>
                    </Box>
                  ))}
                  <div ref={commentsEndRef} />
                </VStack>
              )}
            </Box>

            {/* Add Comment Box */}
            {ticket.status !== "Closed" && (
              <Box p={4} borderTop="1px" borderColor="gray.100" bg="white">
                <Textarea
                  placeholder="Write a comment or reply..."
                  value={newComment}
                  onChange={(e) => setNewComment(e.target.value)}
                  rows={3}
                  mb={2}
                />
                <Flex justify="flex-end">
                  <Button
                    leftIcon={<FiSend />}
                    colorScheme="blue"
                    onClick={handleAddComment}
                    isLoading={submitting}
                    isDisabled={!newComment.trim()}
                  >
                    Send Reply
                  </Button>
                </Flex>
              </Box>
            )}
          </Box>
        </GridItem>

        {/* Sidebar - Ticket Info & Actions */}
        <GridItem>
          <VStack spacing={4} align="stretch">
            {/* Ticket Details Card */}
            <Card>
              <CardBody>
                <Heading size="sm" mb={4}>Ticket Details</Heading>
                
                <VStack spacing={3} align="stretch">
                  <Flex justify="space-between">
                    <Text color="gray.500" fontSize="sm">Ticket ID</Text>
                    <Text fontWeight="medium" fontSize="sm">{ticket.ticketId}</Text>
                  </Flex>
                  
                  <Divider />
                  
                  <Flex justify="space-between">
                    <Text color="gray.500" fontSize="sm">Type</Text>
                    <Badge colorScheme={ticket.type === "Customer" ? "blue" : "purple"}>
                      {ticket.type}
                    </Badge>
                  </Flex>
                  
                  <Flex justify="space-between">
                    <Text color="gray.500" fontSize="sm">Category</Text>
                    <Badge colorScheme="teal">{ticket.category}</Badge>
                  </Flex>
                  
                  <Flex justify="space-between" align="center">
                    <Text color="gray.500" fontSize="sm">Priority</Text>
                    <Menu>
                      <MenuButton as={Button} size="sm" variant="outline">
                        <Badge colorScheme={priorityColorMap[ticket.priority]}>
                          {ticket.priority} ▼
                        </Badge>
                      </MenuButton>
                      <MenuList>
                        {priorityOptions.map(p => (
                          <MenuItem key={p} onClick={() => handleUpdatePriority(p)}>
                            {p}
                          </MenuItem>
                        ))}
                      </MenuList>
                    </Menu>
                  </Flex>
                  
                  <Flex justify="space-between">
                    <Text color="gray.500" fontSize="sm">Status</Text>
                    <Badge colorScheme={statusColorMap[ticket.status]}>{ticket.status}</Badge>
                  </Flex>
                  
                  <Divider />
                  
                  <Flex justify="space-between">
                    <Text color="gray.500" fontSize="sm">Created By</Text>
                    <VStack align="end" spacing={0}>
                      <Text fontSize="sm" fontWeight="medium">{ticket.createdBy?.name || "-"}</Text>
                      <Text fontSize="xs" color="gray.500">{ticket.createdBy?.mobile || "-"}</Text>
                    </VStack>
                  </Flex>
                  
                  <Flex justify="space-between">
                    <Text color="gray.500" fontSize="sm">Assigned To</Text>
                    <Text fontSize="sm">{ticket.assignedTo?.name || "Unassigned"}</Text>
                  </Flex>
                  
                  <Divider />
                  
                  <Flex justify="space-between">
                    <Text color="gray.500" fontSize="sm">Created</Text>
                    <Text fontSize="sm">{formatDate(ticket.createdAt)}</Text>
                  </Flex>
                  
                  {ticket.resolvedAt && (
                    <Flex justify="space-between">
                      <Text color="gray.500" fontSize="sm">Resolved</Text>
                      <Text fontSize="sm">{formatDate(ticket.resolvedAt)}</Text>
                    </Flex>
                  )}
                  
                  {ticket.closedAt && (
                    <Flex justify="space-between">
                      <Text color="gray.500" fontSize="sm">Closed</Text>
                      <Text fontSize="sm">{formatDate(ticket.closedAt)}</Text>
                    </Flex>
                  )}
                </VStack>
              </CardBody>
            </Card>

             {/* Close Ticket Button */}
              {/* Close Ticket */}
                  {ticket.status !== "Closed" && (
                    <Button
                      colorScheme="red"
                      size="sm"
                      onClick={handleCloseTicket}
                    >
                      Close Ticket
                    </Button>
                  )}
          </VStack>
        </GridItem>
      </Grid>

      {/* Assign Ticket Modal */}
      <Modal isOpen={isAssignOpen} onClose={onAssignClose} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Assign Ticket</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl>
                <FormLabel>Select Role</FormLabel>
                <Select 
                  placeholder="Select role" 
                  value={selectedRole} 
                  onChange={(e) => setSelectedRole(e.target.value)}
                >
                  {roles.map(role => (
                    <option key={role._id} value={role._id}>
                      {role.roleName}
                    </option>
                  ))}
                </Select>
              </FormControl>
              <FormControl isDisabled={!selectedRole}>
                <FormLabel>Select User</FormLabel>
                <Select 
                  placeholder={selectedRole ? "Select user" : "Please select a role first"} 
                  value={selectedAssignAdmin} 
                  onChange={(e) => setSelectedAssignAdmin(e.target.value)}
                >
                  {assignModalAdmins.map(admin => (
                    <option key={admin._id} value={admin._id}>
                      {admin.name} - {admin.department || admin.designation || "Admin"}
                    </option>
                  ))}
                </Select>
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onAssignClose}>
              Cancel
            </Button>
            <Button 
              colorScheme="blue" 
              onClick={handleAssignFromModal} 
              isDisabled={!selectedRole || !selectedAssignAdmin}
            >
              Assign
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Stack>
  );
};

export default TicketDetails;

